
import { Col, Row, Card, Form } from 'react-bootstrap';



import { useAppContext } from 'providers/AppProvider';
import SearchBox from 'components/common/SearchBox';

import { faFileExport, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { VisitorsTable } from 'components/tables/VisitorsTable';
import { useState, useMemo, useEffect } from 'react';

import { lazy, Suspense } from 'react';
import SidePanel from './SidePanel';
import BlockCustomerButton from 'components/modules/chat/BlockCustomerButton';
import VisitorsMap from './VisitorsMap';
// Lazy-loaded so three.js (~500 kB) is only fetched when the Globe view is opened.
const VisitorsGlobe = lazy(() => import('./VisitorsGlobe'));
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

const TrafficPage = () => {
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState<any>(null);

  const trafficInformation = useSelector(
    (state: RootState) => state.traffic.customers
  );
  
  const [customerTypes, setcustomerTypes] = useState("all_customers")
  const [countryFilter, setCountryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'list' | 'map' | 'globe'>('list');

  const filteredForMap = useMemo(() => {
    let data = trafficInformation as any[];
    if (customerTypes === 'chatting') data = data.filter(c => c.chat_started);
    else if (customerTypes === 'active') data = data.filter(c => c.active);
    else if (customerTypes === 'inactive') data = data.filter(c => !c.active);
    if (countryFilter !== 'all')
      data = data.filter(c => (c.country || '').toLowerCase() === countryFilter.toLowerCase());
    const q = search.trim().toLowerCase();
    if (q)
      data = data.filter(c =>
        [c.name, c.email, c.page, c.title, c.city]
          .filter(Boolean)
          .some((v: string) => String(v).toLowerCase().includes(q))
      );
    return data;
  }, [trafficInformation, customerTypes, countryFilter, search]);

  // Refresh the relative durations ("time on site" / "latest active") periodically.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick(n => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const stats = useMemo(() => {
    const online = trafficInformation.filter((c: any) => c.active).length;
    const chatting = trafficInformation.filter((c: any) => c.chat_started).length;
    const countries = new Set(
      trafficInformation.map((c: any) => c.country).filter(Boolean)
    ).size;
    return { online, chatting, total: trafficInformation.length, countries };
  }, [trafficInformation]);

  const countryOptions = useMemo(() => {
    const map = new Map<string, string>();
    trafficInformation.forEach((c: any) => {
      if (c.country) {
        const n = (c.country_name || '').trim();
        map.set(c.country, n.length > 2 ? n : c.country.toUpperCase());
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [trafficInformation]);

  const {
    config: { isDark }
  } = useAppContext();

  const handleRowClick = (visitor: any) => {

    console.log("setSelectedVisitor: " , visitor);

    setSelectedVisitor(visitor);
    setIsSidePanelOpen(true);
  };

  const closeSidePanel = () => {
    setIsSidePanelOpen(false);
    setSelectedVisitor(null);
  };

  function displayCustomerName(itemName: any) {
    if (itemName == null) return '';
    let name = 'Unnamed Customer';
    if (itemName.name != undefined) name = itemName.name;
    if (itemName.name == '') name = 'Unnamed Customer';

    return name;
  }

  const getCountryImageSRC = (country: any) => {
    if (country != undefined) {
      const country2 = country.toLowerCase();
      return 'https://cdn.easychatdesk.com/flags/' + country2 + '.svg';
    }

    return '';
  };

  // Full country name as-is, but a 2-letter code uppercased (pt -> PT).
  const prettyCountry = (countryName?: string, code?: string) => {
    const n = (countryName || '').trim();
    if (n.length > 2) return n;
    const c = (n || code || '').trim();
    return c ? c.toUpperCase() : '';
  };

  function displayCurrentUserHistory(cuser: any) {
    if (cuser != null) {
      if (cuser.history == null) return null;

      if (cuser.history.length === 0) return <li>No visits</li>;

      // Reverse the history array before mapping
      return cuser.history
        .slice()
        .reverse()
        .map((historyItem: any) => {
          return (
            <li className="mb-1" key={historyItem.visited_page}>
              <a
                href={historyItem.visited_page}
                title={historyItem.visited_page}
                target="_blank"
                rel="noopener noreferrer"
              >
                {historyItem.title.substring(0, 43)}...
              </a>
            </li>
          );
        });
    } else {
      return <li>No visits</li>;
    }
  }

  return (
    <>
      <Row className="gy-3 mb-6 justify-content-between">
        <Col md={12} xs="auto">
          <h2 className="mb-2 text-body-emphasis">Traffic</h2>
          <h5 className="text-body-tertiary fw-semibold">
            See who is online right now on your website
          </h5>
        </Col>

        <Col md={12}>
          <Row className="g-3">
            {[
              { label: 'Online now', value: stats.online },
              { label: 'In chat', value: stats.chatting },
              { label: 'Visitors (last 2h)', value: stats.total },
              { label: 'Countries', value: stats.countries }
            ].map(s => (
              <Col xs={6} md={3} key={s.label}>
                <Card className="h-100">
                  <Card.Body className="py-3">
                    <div className="fs-9 text-body-tertiary mb-1">{s.label}</div>
                    <div className="fs-4 fw-bold text-body-emphasis lh-1">{s.value}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>

        <Col md={12}>
          <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <div className="btn-group btn-group-sm" role="group">
              <button
                className={`btn btn-${view === 'list' ? 'primary' : 'phoenix-secondary'}`}
                onClick={() => setView('list')}
              >
                List
              </button>
              <button
                className={`btn btn-${view === 'map' ? 'primary' : 'phoenix-secondary'}`}
                onClick={() => setView('map')}
              >
                Map
              </button>
              <button
                className={`btn btn-${view === 'globe' ? 'primary' : 'phoenix-secondary'}`}
                onClick={() => setView('globe')}
                onMouseEnter={() => import('./VisitorsGlobe')}
              >
                Globe
              </button>
            </div>
            <Form.Select
              size="sm"
              style={{ width: 200 }}
              value={countryFilter}
              onChange={e => setCountryFilter(e.target.value)}
            >
              <option value="all">All countries</option>
              {countryOptions.map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </Form.Select>
            <Form.Control
              size="sm"
              placeholder="Search name, email, page…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 280 }}
            />
          </div>

        <ul className="nav nav-pills">
  <li className="nav-item">
    <a onClick={() =>{ setcustomerTypes('all_customers'); }} className={`nav-link ${customerTypes === 'all_customers' ? 'active' : ''}`}  aria-current="page" href="#">All Customers</a>
  </li>

  <li className="nav-item">
    <a onClick={() =>{ setcustomerTypes('active'); }} className={`nav-link ${customerTypes === 'active' ? 'active' : ''}`} href="#">Active</a>
  </li>

  <li className="nav-item">
    <a onClick={() =>{ setcustomerTypes('inactive'); }} className={`nav-link ${customerTypes === 'inactive' ? 'active' : ''}`} href="#">Inactive</a>
  </li>

  <li className="nav-item">
    <a onClick={() =>{ setcustomerTypes('chatting'); }} className={`nav-link ${customerTypes === 'chatting' ? 'active' : ''}`} href="#">Chatting</a>
  </li>
</ul>

        </Col>


        <Col md={12} xs="auto">
          {view === 'map' ? (
            <VisitorsMap visitors={filteredForMap} />
          ) : view === 'globe' ? (
            <Suspense
              fallback={
                <div className="text-center text-body-tertiary py-6">
                  Loading globe…
                </div>
              }
            >
              <VisitorsGlobe visitors={filteredForMap} />
            </Suspense>
          ) : (
            <VisitorsTable
              onRowClick={handleRowClick}
              trafficInformation={trafficInformation}
              customerTypes={customerTypes}
              countryFilter={countryFilter}
              search={search}
            />
          )}
        </Col>
      </Row>

      <SidePanel
        show={isSidePanelOpen}
        onClose={closeSidePanel}
        title="Visitor Details"
      >
        <>
          <div className="w-100 mb-4 mt-2 text-center">
            <div className="circle-avatar-1 text-center">UC</div>
          </div>

          <h4 className="text-center mb-0">
            {displayCustomerName(selectedVisitor)}
          </h4>

          <p className="text-center  mb-0">
            {selectedVisitor?.region}, {selectedVisitor?.city}
          </p>
          <p className="text-center">
            {prettyCountry(selectedVisitor?.country_name, selectedVisitor?.country)}
          </p>

          <div className="text-center">
            <img
              src={getCountryImageSRC(selectedVisitor?.country)}
              width="28"
            />
          </div>

          <div className="text-center mt-3">
            <BlockCustomerButton
              customerId={selectedVisitor?.customer_id}
              blocked={selectedVisitor?.blocked}
            />
          </div>

          <div className={isDark ? 'line-line-1-dark' : 'line-line-1'}></div>

          <div className="w-100">
            <h5 className="card-user-1-title mb-4 mt-3">Visited Pages</h5>
            <div className="">
              <ul className="no-padding-no-margin">
                {displayCurrentUserHistory(selectedVisitor)}
              </ul>
            </div>
          </div>

          <div className={isDark ? 'line-line-1-dark' : 'line-line-1'}></div>

          <div className="w-100">
            <h5 className="card-user-1-title mb-4 mt-3">
              Additional Information
            </h5>
            <div className="w-100">
              <p className="mb-1">Chat Time: 1 minute</p>
              <p className="mb-1">First time visitor: 1 visit</p>
              <p className="mb-1">Chats: no chats</p>
              <p className="mb-1">
                Referral: <b>{selectedVisitor?.refer}</b>
              </p>
              <p className="mb-1">
                Current Page: <b>{selectedVisitor?.title}</b>
              </p>
            </div>
          </div>

          <div className={isDark ? 'line-line-1-dark' : 'line-line-1'}></div>

          <div className="w-100">
            <h5 className="card-user-1-title mb-4 mt-3">
              More Customer Information
            </h5>
            <div className="w-100">
              <p className="mb-1">IP: {selectedVisitor?.ip}</p>
              <p className="mb-1">Browser: {selectedVisitor?.browser}</p>
              <p className="mb-1">Device: {selectedVisitor?.device}</p>
              <p className="mb-1">OS: {selectedVisitor?.os}</p>
            </div>
          </div>
        </>
      </SidePanel>
    </>
  );
};

export default TrafficPage;
